import React from 'react';
import { useParams } from 'react-router-dom';
import { useFetchWithCache } from '../hooks/useFetchWithCache';
import LoadingSpinner from '../components/LoadingSpinner';
import { PageBanner, BannerContent, Eyebrow, SectionTitle, GoldDivider } from './styles/Shared.styles';
import {
  Wrap, BodySection, Inner, Lead,
  ServicesSection, ServiceGrid, ServiceItem,
  Gallery, GalleryGrid, GalleryItem, Empty,
  LogoWrap, ContactSection, ContactGrid, ContactItem,
  AdditionalInfoSection,
} from './styles/CompanyPage.styles';
import { FaEnvelope, FaPhone } from 'react-icons/fa';

const CompanyPage = () => {
  const { slug } = useParams();
  const { data: company, loading, error } = useFetchWithCache(`/companies/${slug}`);

  if (loading && !company) return <LoadingSpinner fullPage />;
  if (error || !company) {
    return (
      <Wrap>
        <PageBanner>
          <BannerContent>
            <span className="eyebrow">Company</span>
            <h1>Not Found</h1>
            <p>We couldn't find that company.</p>
          </BannerContent>
        </PageBanner>
        <Empty>Please return to the homepage and try a different company.</Empty>
      </Wrap>
    );
  }

  const c = company;
  const services = Array.isArray(c.services) ? c.services : [];
  const images = Array.isArray(c.images) ? c.images : [];

  return (
    <Wrap>
      <PageBanner $src={c.bannerImage}>
        <BannerContent>
          <span className="eyebrow">Ehcerex Group</span>
          <h1>{c.title}</h1>
        </BannerContent>
      </PageBanner>

      {c.logo && (
        <LogoWrap>
          <img src={c.logo} alt={`${c.title} logo`} />
        </LogoWrap>
      )}

      <BodySection>
        <Inner>
          <Lead>
            <span className="eyebrow">Overview</span>
            <h2>About <span style={{ fontStyle: 'italic', color: '#9C8033' }}>{c.title}</span></h2>
            <GoldDivider />
            {c.description ? (
              c.description.split('\n').map((p, i) => <p key={i}>{p}</p>)
            ) : (
              <p>Details about this company will be available soon.</p>
            )}
          </Lead>
        </Inner>
      </BodySection>

      {services.length > 0 && (
        <ServicesSection>
          <Inner>
            <Eyebrow>What We Offer</Eyebrow>
            <SectionTitle>Services &amp; <span>Capabilities</span></SectionTitle>
            <GoldDivider />
            <ServiceGrid>
              {services.map((s, i) => (
                <ServiceItem key={i}>
                  <span className="idx">/ {String(i + 1).padStart(2, '0')}</span>
                  {s}
                </ServiceItem>
              ))}
            </ServiceGrid>
          </Inner>
        </ServicesSection>
      )}

      {images.length > 0 && (
        <Gallery>
          <Inner>
            <Eyebrow>Gallery</Eyebrow>
            <SectionTitle>A closer <span>look</span></SectionTitle>
            <GoldDivider />
          </Inner>
          <GalleryGrid>
            {images.map((img, i) => (
              <GalleryItem key={i}>
                <img src={img} alt={`${c.title} ${i + 1}`} />
              </GalleryItem>
            ))}
          </GalleryGrid>
        </Gallery>
      )}

      {(c.email || c.phone) && (
        <ContactSection>
          <Inner>
            <Eyebrow>Get In Touch</Eyebrow>
            <SectionTitle>Contact <span>{c.title}</span></SectionTitle>
            <GoldDivider />
            <ContactGrid>
              {c.email && (
                <ContactItem href={`mailto:${c.email}`}>
                  <span className="icon"><FaEnvelope /></span>
                  <div>
                    <div className="label">Email</div>
                    <div className="value">{c.email}</div>
                  </div>
                </ContactItem>
              )}
              {c.phone && (
                <ContactItem href={`tel:${c.phone.replace(/\s+/g, '')}`}>
                  <span className="icon"><FaPhone /></span>
                  <div>
                    <div className="label">Phone</div>
                    <div className="value">{c.phone}</div>
                  </div>
                </ContactItem>
              )}
            </ContactGrid>
          </Inner>
        </ContactSection>
      )}

      {c.additionalInfo && (
        <AdditionalInfoSection>
          <Inner>
            <Eyebrow>More Information</Eyebrow>
            <SectionTitle>Additional <span>Details</span></SectionTitle>
            <GoldDivider />
            <div className="content">{c.additionalInfo}</div>
          </Inner>
        </AdditionalInfoSection>
      )}
    </Wrap>
  );
};

export default CompanyPage;
